import { IBookingQuoteRevisionLine } from '../interfaces/heavyDutyMachinery/IBooking';
import { IProposedQuoteLine } from '../services/BookingService';
import {
  IQuoteDraftContextLine,
  IQuoteDraftLine,
  QUOTE_PRICE_SOURCE,
  QuotePriceSource,
} from '../interfaces/heavyDutyMachinery/IQuoteDraft';

/**
 * One line while it is being edited.
 *
 * Deliberately its own type rather than IBookingQuoteRevisionLine: a line
 * under the cursor is allowed to be incomplete in ways a saved one never is
 * (an empty amount is a field nobody has filled in yet, which is not the same
 * thing as zero euros), and `key`/`generated` exist only for as long as the
 * editor is open.
 */
export interface IQuoteDraftEditLine {
  /** React identity across reorders and regenerations. Never sent anywhere. */
  key: string;
  title: string;
  description: string;
  amount: number | null;
  /** Base | Delivery | Insurance | Accessories | Operator | Service | Custom. */
  origin: string;
  unit?: string;
  quantity?: number | null;
  unitPrice?: number | null;
  priceSource?: QuotePriceSource;
  catalogKey?: string;
  /**
   * True for a line the assistant produced and the provider has not replaced
   * by hand. It is what lets a second "generate" swap out the assistant's own
   * lines while leaving hand-written ones alone -- regenerating must never
   * quietly delete somebody's typing.
   */
  generated?: boolean;
}

let draftKeySeed = 0;
export const nextQuoteDraftKey = (): string => `line-${(draftKeySeed += 1)}`;

/** A line of a saved revision, opened for editing. */
export const toEditLine = (line: IBookingQuoteRevisionLine): IQuoteDraftEditLine => ({
  key: nextQuoteDraftKey(),
  title: line.title,
  description: line.description ?? '',
  amount: line.amount,
  origin: line.origin,
  unit: line.unit,
  quantity: line.quantity ?? null,
  unitPrice: line.unitPrice ?? null,
  priceSource: line.priceSource,
  catalogKey: line.catalogKey,
});

/**
 * A line the assistant drafted, opened for editing.
 *
 * Always `Custom`: the computed origins map back onto the booking's own cost
 * buckets (the base rate, the delivery, the insurance), and a line invented
 * for a job has no bucket to map onto.
 */
export const draftLineToEditLine = (line: IQuoteDraftLine): IQuoteDraftEditLine => ({
  key: nextQuoteDraftKey(),
  title: line.title,
  description: line.description ?? '',
  amount: line.amount,
  origin: 'Custom',
  unit: line.unit,
  quantity: line.quantity,
  unitPrice: line.unitPrice,
  priceSource: line.priceSource,
  catalogKey: line.catalogKey,
  generated: true,
});

/** A blank hand-written line. */
export const emptyEditLine = (): IQuoteDraftEditLine => ({
  key: nextQuoteDraftKey(),
  title: '',
  description: '',
  amount: null,
  origin: 'Custom',
  priceSource: QUOTE_PRICE_SOURCE.MANUAL,
});

/** True when the line is priced as quantity x unit price rather than as one flat figure. */
export const isMetered = (line: IQuoteDraftEditLine): boolean => line.unitPrice != null;

/**
 * Two decimals, away from zero -- the same rounding the server applies when it
 * re-derives the amount, so the total on screen is the total that gets saved.
 */
export const roundMoney = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

/** What a metered line adds up to; a flat line is just its own amount. */
export const editLineAmount = (line: IQuoteDraftEditLine): number =>
  isMetered(line) ? roundMoney((line.quantity ?? 0) * (line.unitPrice ?? 0)) : line.amount ?? 0;

export const editLinesTotal = (lines: IQuoteDraftEditLine[]): number =>
  roundMoney(lines.reduce((sum, line) => sum + editLineAmount(line), 0));

/**
 * Editing the price by hand makes the line the provider's own, whatever it was
 * before. This is the "manual correction wins" rule the whole rate-card
 * feature rests on: an estimate somebody has looked at and changed is no
 * longer an estimate, and a rate they have overridden on this one job is no
 * longer their catalogue rate.
 */
export const repriced = (line: IQuoteDraftEditLine, patch: Partial<IQuoteDraftEditLine>): IQuoteDraftEditLine => {
  const priceChanged = ('unitPrice' in patch && patch.unitPrice !== line.unitPrice) || ('amount' in patch && patch.amount !== line.amount);
  return {
    ...line,
    ...patch,
    priceSource: priceChanged ? QUOTE_PRICE_SOURCE.MANUAL : line.priceSource,
    // Changing the words is not changing the price, so the source stands --
    // but it does stop the line being the assistant's to replace.
    generated: priceChanged ? false : line.generated,
  };
};

/**
 * What actually goes on the wire.
 *
 * quantity/unitPrice only travel together, because the server derives the
 * amount from them when both are present: sending one without the other would
 * leave it deriving from a missing figure.
 */
export const toProposedLines = (lines: IQuoteDraftEditLine[]): IProposedQuoteLine[] =>
  lines.map((line) => {
    const metered = isMetered(line) && line.quantity != null;
    return {
      title: line.title.trim(),
      description: line.description.trim() || undefined,
      amount: editLineAmount(line),
      origin: line.origin,
      quantity: metered ? line.quantity ?? undefined : undefined,
      unitPrice: metered ? line.unitPrice ?? undefined : undefined,
      unit: metered ? line.unit : undefined,
      priceSource: line.priceSource,
      catalogKey: line.catalogKey,
    };
  });

/** How many lines on the table are still carrying a guessed price. */
export const estimatedCount = (lines: IQuoteDraftEditLine[]): number =>
  lines.filter((line) => line.priceSource === QUOTE_PRICE_SOURCE.ESTIMATED).length;

/**
 * The quote as it stands, in the shape the assistant is given it so a spoken
 * change can be applied.
 *
 * A flat line goes as one of itself at its own price. That is not a fudge: a
 * line somebody wrote as a single figure genuinely is one unit of that thing,
 * and giving the model a quantity it can reason about beats sending it a
 * price with nothing attached.
 */
export const toDraftContextLines = (lines: IQuoteDraftEditLine[]): IQuoteDraftContextLine[] =>
  lines
    .filter((line) => line.title.trim().length > 0)
    .map((line) => ({
      title: line.title.trim(),
      description: line.description.trim() || undefined,
      unit: (isMetered(line) ? line.unit : undefined) ?? 'ud',
      quantity: isMetered(line) ? line.quantity ?? 1 : 1,
      unitPrice: isMetered(line) ? line.unitPrice ?? 0 : line.amount ?? 0,
      priceSource: line.priceSource,
      catalogKey: line.catalogKey,
    }));

/**
 * The lines that come back from a spoken change, put back on screen.
 *
 * A spoken change returns the WHOLE quote, so this is a replacement -- but not
 * a blind one. Two things are carried across by title:
 *
 * `key`, so a line nobody touched keeps its React identity and does not lose
 * the cursor out of it mid-edit.
 *
 * And `generated`, so "volver a sacarlas" still means the same thing
 * afterwards: a line the assistant invented and the change happened to leave
 * alone is still the assistant's to replace, while a line the provider asked
 * for out loud is theirs and survives a regeneration. Without this, one voice
 * edit would either make the whole quote disposable or make none of it
 * replaceable, and both are wrong.
 */
export const mergeDraftedLines = (
  previous: IQuoteDraftEditLine[],
  drafted: IQuoteDraftLine[]
): IQuoteDraftEditLine[] => {
  const byTitle = new Map<string, IQuoteDraftEditLine>();
  previous.forEach((line) => {
    const title = line.title.trim().toLowerCase();
    // First one wins: a duplicated title means we cannot tell which of the two
    // came back, and reaching for the wrong twin is worse than reaching for
    // the first.
    if (title && !byTitle.has(title)) byTitle.set(title, line);
  });

  return drafted.map((line) => {
    const match = byTitle.get(line.title.trim().toLowerCase());
    const fresh = draftLineToEditLine(line);
    return match
      ? { ...fresh, key: match.key, generated: match.generated ?? false }
      : { ...fresh, generated: false };
  });
};
